import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Users, BookOpen, Award, MessageSquare, ExternalLink, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

type AccordionItemProps = {
    item: {
      title: string
      content: string
    }
    isOpen: boolean
    onToggle: () => void
  }

const accordionData = [
  {
    id: 'structure',
    title: 'What is the chapter structure?',
    content: `ACE chapters are student-led academic communities that foster intellectual growth and collaboration. Each chapter consists of:
    
• 15-30 active members from your institution
• Executive board with President, Vice President, Secretary, and Treasurer
• Faculty advisor for guidance and institutional support
• Regular meetings, workshops, and academic events
• Connection to the broader ACE network of chapters worldwide

Chapters operate with autonomy while following ACE's core principles of academic excellence and community building.`
  },
  {
    id: 'process',
    title: 'How are things done?',
    content: `Starting an ACE chapter involves a structured process designed for success:
    
• Submit initial interest form and gather founding members
• Identify and secure a faculty advisor
• Develop chapter constitution and bylaws
• Complete official chapter application with required documentation
• Attend virtual orientation session with ACE leadership
• Receive official charter and welcome package
• Begin regular chapter activities and programming

The entire process typically takes 4-6 weeks from application to charter approval.`
  },
  {
    id: 'requirements',
    title: 'How do I run an ACE Chapter?',
    content: `Running a successful ACE chapter requires commitment and organization:
    
• Hold bi-weekly meetings with consistent attendance
• Organize monthly academic workshops or guest speaker events
• Maintain active communication with members via digital platforms
• Submit quarterly reports to ACE headquarters
• Participate in annual ACE conference and regional events
• Recruit new members each semester
• Collaborate with other campus organizations
• Uphold ACE's values of academic excellence and inclusivity

We provide comprehensive training, resources, and ongoing support to ensure your chapter thrives.`
  },
  {
    id: 'benefits',
    title: 'What are the benefits?',
    content: `ACE chapter leaders and members enjoy numerous advantages:
    
• Leadership development and executive experience
• Access to exclusive academic resources and research opportunities
• Networking with ACE alumni in various industries
• Priority consideration for ACE scholarships and grants
• Recognition on transcripts and LinkedIn profiles
• Professional development workshops and career guidance
• Opportunity to host prestigious academic conferences
• Connection to a global community of academic achievers

These benefits create lasting value for your academic and professional journey.`
  }
];

const AccordionItem = ({ item, isOpen, onToggle }: AccordionItemProps) => {
    return (
      <motion.div 
        className="border border-gray-700 rounded-lg overflow-hidden bg-gray-800/50 backdrop-blur-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
      >
        <button
          onClick={onToggle}
          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-700/30 transition-colors duration-200"
        >
          <h3 className="text-lg font-semibold text-white">{item.title}</h3>
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="w-5 h-5 text-red-500" />
          </motion.div>
        </button>
        
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-4 text-gray-300 leading-relaxed whitespace-pre-line border-t border-gray-700/50 pt-4">
                {item.content}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    )
  }

export default function StartAChapter() {
const [openAccordion, setOpenAccordion] = useState<string | null>('structure');


  const handleAccordionToggle = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Start a <span className="text-red-500">Chapter</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join the academic community of ACE! Ready to lead and inspire? Let's start your chapter journey together.
            </p>
            <p className="text-lg text-gray-400">
              Complete the form below or explore our comprehensive guides to get started.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Left Column - Accordion */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-4">Chapter Information</h2>
              <p className="text-gray-400">
                Everything you need to know about starting and running an ACE chapter at your institution.
              </p>
            </div>

            <div className="space-y-4">
              {accordionData.map((item) => (
                <AccordionItem
                  key={item.id}
                  item={item}
                  isOpen={openAccordion === item.id}
                  onToggle={() => handleAccordionToggle(item.id)}
                />
              ))}
            </div>
          </motion.div>

          {/* Right Column - Embedded Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            
            {/* Google Slides Embed */}
            <Card className="bg-gray-800/50 border-gray-700 overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <Play className="w-5 h-5 text-red-500" />
                  Chapter Setup Guide
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Interactive presentation covering all aspects of chapter creation
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                  <iframe
                    src="https://docs.google.com/presentation/d/e/2PACX-1vQKVx9HpqCX5YJNm5YvBz3iXMn2bU1x4wUjOGH9Pz-Hn5rL8Fv6Kp9-4F2gT8Rm6B7Kz2Xq9wL4pY8/embed?start=false&loop=false&delayms=3000"
                    className="w-full h-full border-0"
                    allowFullScreen={true}
                    title="ACE Chapter Setup Guide"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Application Form */}
            <Card className="bg-gray-800/50 border-gray-700 overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-red-500" />
                  Chapter Application Form
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  Submit your official chapter application here
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="aspect-[4/5] bg-gray-900 rounded-lg overflow-hidden">
                  <iframe
                    src="https://docs.google.com/forms/d/e/1FAIpQLSdVqHcL4xF8YW2zE7hK9mN3pQ6rT5sA1bC8dG7jL2eR4fV6nO9/viewform?embedded=true"
                    className="w-full h-full border-0"
                    title="ACE Chapter Application Form"
                  >
                    Loading...
                  </iframe>
                </div>
              </CardContent>
            </Card>

          </motion.div>
        </div>

        {/* Additional Information Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Additional Resources</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore more resources to help you succeed in starting and managing your ACE chapter.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            
            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:border-red-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-500/30 transition-colors">
                  <Users className="w-6 h-6 text-red-500" />
                </div>
                <CardTitle className="text-white">Member Recruitment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Learn effective strategies for finding and recruiting passionate members for your chapter.
                </p>
                <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-red-500 hover:border-red-500">
                  View Guide <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:border-red-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-500/30 transition-colors">
                  <BookOpen className="w-6 h-6 text-red-500" />
                </div>
                <CardTitle className="text-white">Event Planning</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Access templates and guides for organizing successful academic events and workshops.
                </p>
                <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-red-500 hover:border-red-500">
                  Get Templates <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:border-red-500/50 transition-all duration-300 group">
              <CardHeader>
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-500/30 transition-colors">
                  <Award className="w-6 h-6 text-red-500" />
                </div>
                <CardTitle className="text-white">Support Network</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 mb-4">
                  Connect with experienced chapter leaders and access ongoing mentorship opportunities.
                </p>
                <Button variant="outline" className="w-full border-gray-600 text-white hover:bg-red-500 hover:border-red-500">
                  Join Network <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4">Ready to Get Started?</h3>
            <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
              Have questions or need additional support? Our team is here to help you every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3">
                Contact Support
              </Button>
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-700 px-8 py-3">
                Schedule a Call
              </Button>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}